import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { BankPicker } from '@/components/BankPicker'
import { useAuth } from '@/lib/auth'
import {
  createOrder,
  getDashboard,
  getExchanges,
  getOrders,
  uploadScreenshots,
  type OrderOut,
} from '@/lib/api'
import { t } from '@/i18n'
import { formatDecimalInput, formatMoney, multiply, parseAmount } from '@/lib/format'
import { isNativeApp, pickNativeImage } from '@/lib/native'

type Tab = 'manual' | 'screen'
type DealStatus = 'completed' | 'paid' | 'appeal'

const inputClass =
  'mt-1 w-full min-h-11 rounded-md border border-surface-border bg-surface px-2 py-1.5 text-base text-slate-100'

export function DealSheet({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<Tab>('manual')
  const { me } = useAuth()
  const canOperate = ['owner', 'admin', 'operator'].includes(me?.organization.role ?? '')

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="mma-sheet-root">
      <button type="button" className="mma-sheet-backdrop" aria-label={t('sheet.close')} onClick={onClose} />
      <div className="mma-sheet" role="dialog" aria-modal="true" aria-labelledby="deal-sheet-title">
        <div className="flex items-center justify-between gap-2">
          <h2 id="deal-sheet-title" className="text-base font-semibold text-slate-100">
            {t('quick.title')}
          </h2>
          <button type="button" className="mma-sheet-close" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
        <div className="mma-sheet-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'manual'}
            className={tab === 'manual' ? 'mma-sheet-tab mma-sheet-tab-on' : 'mma-sheet-tab'}
            onClick={() => setTab('manual')}
          >
            {t('sheet.manual')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'screen'}
            className={tab === 'screen' ? 'mma-sheet-tab mma-sheet-tab-on' : 'mma-sheet-tab'}
            onClick={() => setTab('screen')}
          >
            {t('sheet.screen')}
          </button>
        </div>
        {!canOperate ? (
          <p className="mt-4 text-sm text-slate-400">{t('sheet.noRight')}</p>
        ) : tab === 'manual' ? (
          <ManualDeal onClose={onClose} />
        ) : (
          <ScreenDeal onClose={onClose} />
        )}
      </div>
    </div>
  )
}

function ManualDeal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const dash = useQuery({ queryKey: ['dashboard', 'today'], queryFn: () => getDashboard({ period: 'today' }) })
  const last = useQuery({ queryKey: ['orders', 'latest'], queryFn: () => getOrders({ limit: '1' }) })
  const exchanges = useQuery({ queryKey: ['exchanges'], queryFn: getExchanges })
  const data = dash.data
  const latest: OrderOut | undefined = last.data?.items[0]

  const [side, setSide] = useState<'buy' | 'sell'>('buy')
  const [exchange, setExchange] = useState('')
  const [bankId, setBankId] = useState<string | null>(null)
  const [quantity, setQuantity] = useState('')
  const [price, setPrice] = useState('')
  const [priceTouched, setPriceTouched] = useState(false)
  const [counterparty, setCounterparty] = useState('')
  const [status, setStatus] = useState<DealStatus>('completed')
  const [error, setError] = useState<string | null>(null)

  const asset = latest?.asset_code || data?.asset_code || 'USDT'
  const fiat = latest?.fiat_code || data?.last_buy_fiat || data?.last_sell_fiat || data?.primary_code || 'PLN'

  useEffect(() => {
    if (exchange) return
    const fromLast =
      (side === 'buy' ? data?.last_buy_exchange : data?.last_sell_exchange) || latest?.exchange_code
    setExchange(fromLast || 'binance')
  }, [data?.last_buy_exchange, data?.last_sell_exchange, exchange, latest?.exchange_code, side])

  useEffect(() => {
    if (bankId !== null || !latest?.bank_id) return
    setBankId(latest.bank_id)
  }, [bankId, latest?.bank_id])

  useEffect(() => {
    if (priceTouched) return
    const fromDash = side === 'buy' ? data?.last_buy_price : data?.last_sell_price
    const raw = fromDash || latest?.price
    if (raw) setPrice(formatDecimalInput(raw))
  }, [data?.last_buy_price, data?.last_sell_price, latest?.price, priceTouched, side])

  const total = useMemo(() => multiply(quantity, price), [price, quantity])

  const save = useMutation({
    mutationFn: async () => {
      const qty = parseAmount(quantity)
      const unit = parseAmount(price)
      if (!qty || !unit) throw new Error(t('quick.needQty'))
      if (!exchange) throw new Error(t('quick.needEx'))
      if (!bankId) throw new Error(t('quick.needBank'))
      return createOrder({
        occurred_at: new Date().toISOString(),
        exchange_code: exchange,
        side,
        asset_code: asset,
        quantity: qty,
        price: unit,
        fiat_code: fiat,
        fiat_amount: total ? String(total) : null,
        bank_id: bankId,
        counterparty: counterparty.trim() || null,
        order_status: status,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
      void queryClient.invalidateQueries({ queryKey: ['freshness'] })
      void queryClient.invalidateQueries({ queryKey: ['reconciliation'] })
      onClose()
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : t('quick.fail'))
    },
  })

  return (
    <form
      className="mt-4"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <p className="text-[11px] text-slate-500">
        {t('quick.hint', { pair: `${asset}/${fiat}` })}
      </p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <label className="text-[11px] text-slate-500">
          {t('common.side')}
          <select
            value={side}
            onChange={(event) => {
              setSide(event.target.value as 'buy' | 'sell')
              setPriceTouched(false)
            }}
            className={inputClass}
          >
            <option value="buy">{t('common.buy')}</option>
            <option value="sell">{t('common.sell')}</option>
          </select>
        </label>
        <label className="text-[11px] text-slate-500">
          {t('common.exchange')}
          <select value={exchange} onChange={(event) => setExchange(event.target.value)} className={inputClass}>
            {(exchanges.data ?? []).map((item) => (
              <option key={item.code} value={item.code}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <div className="col-span-2 text-[11px] text-slate-500">
          {t('common.bank')}
          <div className="mt-1">
            <BankPicker fiat={fiat} value={bankId} onChange={(id) => setBankId(id)} />
          </div>
        </div>
        <label className="text-[11px] text-slate-500">
          {t('quick.qty', { asset })}
          <input
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            className={`${inputClass} num`}
            inputMode="decimal"
            placeholder="1000"
          />
        </label>
        <label className="text-[11px] text-slate-500">
          {t('common.rate')}
          <input
            value={price}
            onChange={(event) => {
              setPriceTouched(true)
              setPrice(event.target.value)
            }}
            className={`${inputClass} num`}
            inputMode="decimal"
            placeholder="3.80"
          />
        </label>
        <label className="col-span-2 text-[11px] text-slate-500">
          {t('common.counterparty')}
          <input
            value={counterparty}
            onChange={(event) => setCounterparty(event.target.value)}
            className={inputClass}
            placeholder={t('common.optional')}
          />
        </label>
      </div>
      <p className="mt-3 text-[11px] text-slate-500">{t('sheet.statusHint')}</p>
      <div className="mt-1 grid grid-cols-3 gap-1">
        {(
          [
            ['completed', t('sheet.done')],
            ['paid', t('sheet.active')],
            ['appeal', t('sheet.appeal')],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={status === value ? 'mma-sheet-chip mma-sheet-chip-on' : 'mma-sheet-chip'}
            onClick={() => setStatus(value)}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500">
        {t('quick.sum', { fiat, value: total ? formatMoney(String(total), fiat) : '—' })}
      </p>
      {error && <p className="mt-2 text-sm text-loss">{error}</p>}
      <button type="submit" className="cabin-cta mt-4" disabled={save.isPending}>
        {save.isPending ? t('common.saving') : side === 'buy' ? t('sheet.saveBuy') : t('sheet.saveSell')}
      </button>
    </form>
  )
}

function ScreenDeal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const cameraRef = useRef<HTMLInputElement>(null)
  const galleryRef = useRef<HTMLInputElement>(null)
  const [isPrivate, setIsPrivate] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const upload = useMutation({
    mutationFn: (files: File[]) => uploadScreenshots(files, isPrivate),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['extraction'] })
      const job = data[0]?.job
      onClose()
      if (job?.id) navigate(`/screenshots/${job.id}`)
      else navigate('/screenshots')
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : t('sheet.uploadFail'))
    },
  })

  const take = (list: FileList | null) => {
    if (!list) return
    const files = Array.from(list).filter(
      (file) => file.type.startsWith('image/') || file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'),
    )
    if (files.length === 0) {
      setError(t('sheet.needFile'))
      return
    }
    setError(null)
    upload.mutate(files)
  }

  const takeNative = async (source: 'camera' | 'gallery') => {
    setError(null)
    try {
      const file = await pickNativeImage(source)
      upload.mutate([file])
    } catch (err) {
      const message = err instanceof Error ? err.message : t('sheet.camCancel')
      if (/cancel|скасов/i.test(message)) return
      setError(message)
    }
  }

  const native = isNativeApp()

  return (
    <div className="mt-4">
      <p className="text-sm text-slate-400">
        {t('sheet.shotLead')}
      </p>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          className="cabin-cta"
          disabled={upload.isPending}
          onClick={() => (native ? void takeNative('camera') : cameraRef.current?.click())}
        >
          {t('sheet.camera')}
        </button>
        <button
          type="button"
          className="mma-sheet-secondary"
          disabled={upload.isPending}
          onClick={() => (native ? void takeNative('gallery') : galleryRef.current?.click())}
        >
          {t('sheet.gallery')}
        </button>
      </div>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(event) => {
          take(event.target.files)
          event.target.value = ''
        }}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*,application/pdf,.pdf"
        className="hidden"
        onChange={(event) => {
          take(event.target.files)
          event.target.value = ''
        }}
      />
      <label className="mt-4 flex min-h-11 cursor-pointer items-center gap-2 text-sm text-slate-400">
        <input
          type="checkbox"
          className="rounded border-surface-border"
          checked={isPrivate}
          onChange={(event) => setIsPrivate(event.target.checked)}
        />
        {t('sheet.private')}
      </label>
      {upload.isPending && <p className="mt-3 text-sm text-slate-500">{t('sheet.uploading')}</p>}
      {error && <p className="mt-3 text-sm text-loss">{error}</p>}
    </div>
  )
}
